import * as THREE from 'three';
import { createPRNG } from '../../core/math';
import {
  createLagoonWaterMaterial,
  createSkyDomeMaterial,
  createStoneMaterial,
  createTemporalUniforms,
} from '../../shaders/materials';
import {
  createNautilusShellGeometry,
  createTurritellaShellGeometry,
} from '../../procedural/seashells';
import {
  createSpiralStaircaseTower,
  type SpiralTowerResult,
} from '../../procedural/staircases';
import { createLuthierPavilion } from '../../procedural/violins';
import {
  createBrassTelescope,
  type TelescopeResult,
} from '../../procedural/telescopes';
import { ISIDORA_POIS, ISIDORA_SPECIMENS } from './metadata';
import { createIsidoraPlazaLandmarks } from './plaza';
import type {
  CityDefinition,
  CityInstance,
  InteractiveElement,
  MotifId,
} from '../../core/types';

function buildIsidoraCity(scene: THREE.Scene): CityInstance {
  const root = new THREE.Group();
  root.name = 'CityOfIsidora';
  scene.add(root);

  const uniforms = createTemporalUniforms();
  const interactables: InteractiveElement[] = [];
  const rng = createPRNG(197202);

  // 1. Sky Dome (Added to scene root so both City and Specimen modes share the twilight sky!)
  const skyGeo = new THREE.SphereGeometry(210, 48, 32);
  const skyMat = createSkyDomeMaterial(uniforms);
  const skyDome = new THREE.Mesh(skyGeo, skyMat);
  scene.add(skyDome);

  // Reflective Nocturnal Lagoon Basin
  const waterGeo = new THREE.CircleGeometry(180, 64);
  waterGeo.rotateX(-Math.PI / 2);
  const waterMat = createLagoonWaterMaterial(uniforms);
  const waterMesh = new THREE.Mesh(waterGeo, waterMat);
  waterMesh.position.y = -1.25;
  root.add(waterMesh);

  // 2. Scene Lighting (Balanced key + front-fill + warm plaza uplight)
  const hemiLight = new THREE.HemisphereLight(0xf3a871, 0x241830, 1.15);
  root.add(hemiLight);

  const dirLight = new THREE.DirectionalLight(0xffbe7d, 1.95);
  dirLight.position.set(22, 34, 24);
  dirLight.castShadow = true;
  dirLight.shadow.mapSize.set(2048, 2048);
  dirLight.shadow.camera.near = 5;
  dirLight.shadow.camera.far = 110;
  const d = 28;
  dirLight.shadow.camera.left = -d;
  dirLight.shadow.camera.right = d;
  dirLight.shadow.camera.top = d;
  dirLight.shadow.camera.bottom = -d;
  root.add(dirLight);

  const rimDirLight = new THREE.DirectionalLight(0xb87cff, 0.85);
  rimDirLight.position.set(-24, 28, -20);
  root.add(rimDirLight);

  const citadelWarmLight = new THREE.PointLight(0xff9b42, 24, 45, 1.2);
  citadelWarmLight.position.set(0, 11, 2);
  root.add(citadelWarmLight);

  // 3. Nine Spiral Staircase Towers
  const sharedNautilus = createNautilusShellGeometry(0.32, 0.85);
  const sharedTurritella = createTurritellaShellGeometry(0.36, 0.85);

  const towerSpecs = [
    { x: -1.8, z: -5.2, r: 2.35, h: 18.8, turns: 2.6, steps: 68, cw: true, crown: 'open-terrace' as const },
    { x: -5.6, z: -2.2, r: 1.85, h: 13.6, turns: 2.3, steps: 54, cw: false, crown: 'nautilus-dome' as const },
    { x: 6.8, z: -2.2, r: 1.95, h: 8.2, turns: 1.6, steps: 38, cw: true, crown: 'open-terrace' as const },
    { x: 4.5, z: -8.6, r: 1.65, h: 14.8, turns: 2.2, steps: 52, cw: false, crown: 'nautilus-dome' as const },
    { x: -10.5, z: -7.5, r: 1.55, h: 11.4, turns: 1.9, steps: 42, cw: true, crown: 'lantern-cupola' as const },
    { x: 11.2, z: -5.8, r: 1.48, h: 10.6, turns: 1.8, steps: 38, cw: true, crown: 'nautilus-dome' as const },
    { x: -9.8, z: 2.2, r: 1.42, h: 9.2, turns: 1.6, steps: 34, cw: false, crown: 'lantern-cupola' as const },
    { x: 9.8, z: 4.6, r: 1.45, h: 8.8, turns: 1.5, steps: 32, cw: false, crown: 'nautilus-dome' as const },
    { x: 0.4, z: -12.5, r: 1.72, h: 15.5, turns: 2.4, steps: 56, cw: true, crown: 'nautilus-dome' as const },
  ];

  const towers: SpiralTowerResult[] = [];
  let totalShells = 0;
  let totalLanterns = 0;

  towerSpecs.forEach((spec, idx) => {
    const tower = createSpiralStaircaseTower(
      {
        radius: spec.r,
        height: spec.h,
        turns: spec.turns,
        stepsCount: spec.steps,
        clockwise: spec.cw,
        seedOffset: idx * 1.37 + rng(),
        crownType: spec.crown,
        sharedNautilusGeo: sharedNautilus,
        sharedTurritellaGeo: sharedTurritella,
      },
      uniforms
    );
    tower.group.position.set(spec.x, 0, spec.z);
    root.add(tower.group);
    towers.push(tower);
    totalShells += tower.shellCount;
    totalLanterns += tower.lanternCount;
    interactables.push(...tower.interactables);
  });

  // 4. High Viaduct Bridges
  const bridgeMat = createStoneMaterial(uniforms, {
    baseDesireHex: 0x6f4a51,
    baseMemoryHex: 0x2c3c58,
    roughness: 0.62,
    metalness: 0.16,
  });

  const bridgePairs: Array<[number, number, number]> = [
    [0, 1, 11.2],
    [0, 2, 7.8],
    [0, 3, 12.6],
    [2, 3, 7.9],
    [1, 4, 8.8],
    [3, 8, 11.5],
  ];

  for (const [idxA, idxB, yHeight] of bridgePairs) {
    const a = towerSpecs[idxA];
    const b = towerSpecs[idxB];
    const pA = new THREE.Vector3(a.x, yHeight, a.z);
    const pB = new THREE.Vector3(b.x, yHeight, b.z);
    const dist = pA.distanceTo(pB);
    const mid = pA.clone().lerp(pB, 0.5);

    const spanGeo = new THREE.BoxGeometry(0.68, 0.36, dist);
    const span = new THREE.Mesh(spanGeo, bridgeMat);
    span.position.copy(mid);
    span.lookAt(pB);
    span.castShadow = true;
    root.add(span);
  }

  // 5. The Luthier's Acoustic Loft & Parametric Cremonese Violins
  const luthierPavilion = createLuthierPavilion(uniforms);
  luthierPavilion.group.position.set(6.8, 8.2, -2.2);
  luthierPavilion.group.rotation.y = -0.35;
  root.add(luthierPavilion.group);
  interactables.push(...luthierPavilion.interactables);

  // 6. The Brass Observatory & Keplerian Telescopes
  const telescopes: TelescopeResult[] = [];
  const telescopePlacements = [
    { x: -1.8, y: 18.8, z: -5.2, scale: 1.15, az: 0.35 },
    { x: 0.4, y: 15.5, z: -12.5, scale: 0.85, az: -0.6 },
    { x: -10.5, y: 11.4, z: -7.5, scale: 0.8, az: 0.9 },
  ];

  for (const tp of telescopePlacements) {
    const scope = createBrassTelescope(uniforms, tp.scale, tp.az, true);
    scope.group.position.set(tp.x, tp.y, tp.z);
    root.add(scope.group);
    telescopes.push(scope);
    interactables.push(...scope.interactables);
  }

  // 7. Ground Landmarks
  const plazaLandmarks = createIsidoraPlazaLandmarks(uniforms);
  root.add(plazaLandmarks.group);
  interactables.push(...plazaLandmarks.interactables);
  totalLanterns += plazaLandmarks.lanternsCount;

  // 8. Atmospheric Floating Lantern Embers & Starlight Motes
  const particleCount = 420;
  const particlePositions = new Float32Array(particleCount * 3);

  for (let i = 0; i < particleCount; i++) {
    const theta = rng() * Math.PI * 2;
    const r = 2.0 + rng() * 26.0;
    particlePositions[i * 3] = Math.cos(theta) * r;
    particlePositions[i * 3 + 1] = 0.5 + rng() * 28.0;
    particlePositions[i * 3 + 2] = Math.sin(theta) * r;
  }

  const particleCanvas = document.createElement('canvas');
  particleCanvas.width = 64;
  particleCanvas.height = 64;
  const pCtx = particleCanvas.getContext('2d');
  if (pCtx) {
    const grad = pCtx.createRadialGradient(32, 32, 2, 32, 32, 30);
    grad.addColorStop(0, 'rgba(255, 255, 255, 1.0)');
    grad.addColorStop(0.35, 'rgba(255, 225, 175, 0.65)');
    grad.addColorStop(1, 'rgba(255, 180, 100, 0.0)');
    pCtx.fillStyle = grad;
    pCtx.fillRect(0, 0, 64, 64);
  }
  const particleTex = new THREE.CanvasTexture(particleCanvas);

  const particleGeo = new THREE.BufferGeometry();
  const particlePosAttr = new THREE.BufferAttribute(particlePositions, 3);
  particleGeo.setAttribute('position', particlePosAttr);
  const particleMat = new THREE.PointsMaterial({
    color: 0xffca7a,
    map: particleTex,
    size: 0.48,
    transparent: true,
    opacity: 0.85,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const particleSystem = new THREE.Points(particleGeo, particleMat);
  root.add(particleSystem);
  totalLanterns += particleCount;

  const desireSkyLight = new THREE.Color(0xf3a36b);
  const memorySkyLight = new THREE.Color(0x5288d4);
  const desireGroundLight = new THREE.Color(0x281524);
  const memoryGroundLight = new THREE.Color(0x0b1528);
  const desireCitadel = new THREE.Color(0xff9b42);
  const memoryCitadel = new THREE.Color(0x4c90ff);

  return {
    root,
    interactables,
    temporalUniforms: uniforms,
    update: (elapsed, dt, temporal) => {
      uniforms.uTime.value = elapsed;
      uniforms.uMemory.value = temporal;
      uniforms.uResonance.value = Math.max(0, uniforms.uResonance.value - dt * 0.55);

      hemiLight.color.copy(desireSkyLight).lerp(memorySkyLight, temporal);
      hemiLight.groundColor.copy(desireGroundLight).lerp(memoryGroundLight, temporal);
      dirLight.color.copy(desireSkyLight).lerp(memorySkyLight, temporal);
      citadelWarmLight.color.copy(desireCitadel).lerp(memoryCitadel, temporal);
      particleMat.color.copy(desireCitadel).lerp(memorySkyLight, temporal);

      for (const t of towers) {
        t.update(elapsed, temporal);
      }
      luthierPavilion.update(elapsed, dt);
      for (const s of telescopes) {
        s.update(elapsed, temporal);
      }
      plazaLandmarks.update(elapsed, temporal);

      for (let i = 0; i < particleCount; i++) {
        let y = particlePosAttr.getY(i) + dt * (0.42 - 0.18 * temporal);
        if (y > 29.0) y = 0.5;
        particlePosAttr.setY(i, y);
      }
      particlePosAttr.needsUpdate = true;
      particleSystem.rotation.y = elapsed * 0.025;
    },
    triggerMotifRipple: (_motif: MotifId, worldOrigin?: THREE.Vector3) => {
      if (worldOrigin) {
        uniforms.uPulseOrigin.value.copy(worldOrigin);
      }
      uniforms.uResonance.value = 1.0;
    },
    setWireframe: (enabled: boolean) => {
      root.traverse((obj) => {
        if (obj instanceof THREE.Mesh && obj !== skyDome) {
          const mat = obj.material;
          if (Array.isArray(mat)) {
            mat.forEach((m) => {
              if ('wireframe' in m) m.wireframe = enabled;
            });
          } else if (mat && 'wireframe' in mat) {
            mat.wireframe = enabled;
          }
        }
      });
    },
    getTelemetry: () => ({
      cityId: 'isidora',
      spiralTowers: towers.length,
      encrustedSeashells: totalShells,
      violinsAndStrings: 11,
      telescopes: telescopes.length,
      lanternsAndStars: totalLanterns,
    }),
    dispose: () => {
      scene.remove(skyDome);
      sharedNautilus.dispose();
      sharedTurritella.dispose();
      skyGeo.dispose();
      waterGeo.dispose();
      particleGeo.dispose();
    },
  };
}

export const isidoraCityDefinition: CityDefinition = {
  id: 'isidora',
  name: 'Isidora',
  zhName: '伊西多拉 ISIDORA',
  italianName: 'Le città e la memoria · 2',
  zhCategory: '卡爾維諾 · 城市與記憶 · 二',
  chapter: 1,
  category: 'Cities & Memory',
  categoryIndex: 2,
  epigraph:
    'When a man rides a long time through wild regions he feels the desire for a city. Finally he comes to Isidora, a city where the buildings have spiral staircases encrusted with spiral seashells, where perfect telescopes and violins are made, where the foreigner hesitating between two women always encounters a third, where cockfights degenerate into bloody brawls among the bettors.',
  closingLine:
    'He was thinking of all these things when he desired a city. Isidora, therefore, is the city of his dreams: with one difference. The dreamed-of city contained him as a young man; he arrives at Isidora in his old age. In the square there is the wall where the old men sit and watch the young go by; he is seated in a row with them. Desires are already memories.',
  pois: ISIDORA_POIS,
  specimens: ISIDORA_SPECIMENS,
  build: buildIsidoraCity,
};
